import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { type UserRole, ROLE_RANK } from '@kainos/shared';
import { DEMO_USER } from '@/mocks/fixtures/session';
import { config } from '../config.js';
import { getMe } from './api.js';
import {
  completeNewPassword as cognitoCompleteNewPassword,
  hasSession,
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  type SignInResult,
} from './cognito.js';
import {
  clearSession,
  getCurrentUser,
  onSessionExpired,
  setSession,
  type SessionUser,
} from './session.js';

/**
 * `checking` is the state that matters: on a reload the app cannot know whether
 * a session exists until Cognito has been asked, and rendering the sign-in
 * screen in the meantime would flash it at people who are already signed in.
 */
export type AuthStatus = 'checking' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  user: SessionUser | null;
  status: AuthStatus;
  isAuthenticated: boolean;
  /** True when the user's role is at least `role`. */
  can: (role: UserRole) => boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Auth state for the app.
 *
 * Two systems have to agree before someone is "signed in": Cognito must hold a
 * valid token, and KainOS must know who that token belongs to. This provider is
 * where the two are joined — a token without a KainOS profile is treated as not
 * signed in, because every screen needs the organization that profile carries.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('checking');

  // React runs effects twice in strict mode; the flag keeps a discarded
  // bootstrap from writing state after its replacement already has.
  const activeRef = useRef(0);

  const applySession = useCallback((next: SessionUser) => {
    setSession(next);
    setUser(next);
    setStatus('authenticated');
  }, []);

  const forget = useCallback(() => {
    clearSession();
    setUser(null);
    setStatus('anonymous');
  }, []);

  /** Turns a fresh Cognito token into a KainOS session. */
  const loadProfile = useCallback(async () => {
    const me = await getMe();
    applySession(me);
  }, [applySession]);

  useEffect(() => {
    const run = ++activeRef.current;
    const stale = () => activeRef.current !== run;

    void (async () => {
      if (config.useMocks) {
        if (!stale()) applySession(DEMO_USER);
        return;
      }

      // The cached profile paints the shell immediately; the call below is what
      // actually decides whether the session is still good.
      const cached = getCurrentUser();
      if (cached && !stale()) setUser(cached);

      try {
        if (!(await hasSession())) {
          if (!stale()) forget();
          return;
        }
        const me = await getMe();
        if (!stale()) applySession(me);
      } catch {
        // A token Cognito still accepts but KainOS will not serve — revoked
        // membership, deleted profile — is not a usable session.
        cognitoSignOut();
        if (!stale()) forget();
      }
    })();

    return () => {
      activeRef.current += 1;
    };
  }, [applySession, forget]);

  // The API is the only thing that finds out a session died; when it says so,
  // drop it here so the guard sends them back to sign in.
  useEffect(() => {
    if (config.useMocks) return;

    return onSessionExpired(() => {
      cognitoSignOut();
      forget();
    });
  }, [forget]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<SignInResult> => {
      const result = await cognitoSignIn(email, password);
      if (result.status === 'SIGNED_IN') await loadProfile();
      return result;
    },
    [loadProfile],
  );

  const completeNewPassword = useCallback(
    async (newPassword: string) => {
      await cognitoCompleteNewPassword(newPassword);
      await loadProfile();
    },
    [loadProfile],
  );

  const signOut = useCallback(() => {
    cognitoSignOut();
    forget();
  }, [forget]);

  const can = useCallback(
    (role: UserRole) => (user ? ROLE_RANK[user.role] >= ROLE_RANK[role] : false),
    [user],
  );

  const value = useMemo(
    () => ({
      user,
      status,
      isAuthenticated: status === 'authenticated',
      can,
      signIn,
      completeNewPassword,
      signOut,
    }),
    [user, status, can, signIn, completeNewPassword, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}

/** Convenience for the many places that only need the signed-in person. */
export function useCurrentUser(): SessionUser | null {
  return useAuth().user;
}
